/**
 * # View Islands
 *
 * This file defines some components that allow each UI area to be defined inside each View route as shown below,
 * and the Workbench is responsible for rendering these areas into their containers.
 *
 * ```tsx
 * const MyView = () => {
 *   return <>
 *     <ViewHeader>
 *       ...
 *     </ViewHeader>
 *     <ViewBody>
 *       ...
 *     </ViewBody>
 *     <ViewSidebarTab tabId="my-tab" icon={<MyIcon />}>
 *       ...
 *     </ViewSidebarTab>
 *   </>
 * }
 *
 * const viewRoute = [
 *   {
 *     path: '/my-view',
 *     component: MyView,
 *   }
 * ]
 * ```
 *
 * Each Island is divided into `Target` and `Provider`.
 * The `Provider` wraps the content to be rendered, while the `Target` is placed where it needs to be rendered.
 * Then you get a view portal.
 */

import { createIsland, type Island } from '@affine/core/utils/island';
import type { UiObjectRef } from '@streamlain/copilot-realtime-contract';
import { useLiveData, useService } from '@toeverything/infra';
import type React from 'react';
import {
  createContext,
  forwardRef,
  type Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useSemanticDomRegion } from '../../streamlain/copilot/semantic-dom-region';
import { WorkspaceNavigationPresence } from '../../streamlain/copilot/workspace-navigation-presence';
import { WorkspaceViewContextService } from '../../streamlain/copilot/workspace-view-context';
import type { View } from '../entities/view';
import { ViewService } from '../services/view';
import { WorkbenchService } from '../services/workbench';
import { useViewContentInline } from './use-view-content-inline';

/**
 * Several providers can hold one key for a moment: a route's pending fallback
 * names the same header and body as the content it stands in for. The provider
 * that came first keeps the place, a later one takes it once the first is gone.
 */
interface ViewIslandRegistry {
  [key: string]: readonly Island[] | undefined;
}

/**
 * A registry context will be placed at the top level of the workbench.
 *
 * The `View` will create islands and place them in the registry,
 * while `Workbench` can use the KEY to retrieve and display the islands.
 */
const ViewIslandRegistryContext = createContext<ViewIslandRegistry>({});
const ViewIslandSetContext = createContext<React.Dispatch<
  React.SetStateAction<ViewIslandRegistry>
> | null>(null);

export const ViewIsland = ({
  id,
  children,
}: React.PropsWithChildren<{ id: string }>) => {
  const setter = useContext(ViewIslandSetContext);

  if (!setter) {
    throw new Error(
      'ViewIslandProvider must be used inside ViewIslandRegistryProvider'
    );
  }

  const [island] = useState<Island>(createIsland);

  useEffect(() => {
    setter(prev => ({ ...prev, [id]: [...(prev[id] ?? []), island] }));

    return () => {
      setter(prev => {
        const others = (prev[id] ?? []).filter(other => other !== island);
        const next = { ...prev };
        if (others.length > 0) next[id] = others;
        else delete next[id];
        return next;
      });
    };
  }, [id, island, setter]);

  return <island.Provider>{children}</island.Provider>;
};

export const ViewIslandTarget = forwardRef(function ViewIslandTarget(
  {
    id,
    children,
    ...otherProps
  }: { id: string } & React.HTMLProps<HTMLDivElement>,
  ref: Ref<HTMLDivElement>
) {
  const island = useContext(ViewIslandRegistryContext)[id]?.[0];
  if (!island) {
    return <div ref={ref} {...otherProps} />;
  }

  return (
    <island.Target ref={ref} {...otherProps}>
      {children}
    </island.Target>
  );
});

export const ViewIslandRegistryProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [contextValue, setContextValue] = useState<ViewIslandRegistry>({});

  return (
    <ViewIslandRegistryContext.Provider value={contextValue}>
      <ViewIslandSetContext.Provider value={setContextValue}>
        {children}
      </ViewIslandSetContext.Provider>
    </ViewIslandRegistryContext.Provider>
  );
};

export const ViewBody = ({
  children,
  objectRef,
}: React.PropsWithChildren<{ objectRef?: UiObjectRef }>) => {
  const view = useService(ViewService).view;
  return (
    <ViewIsland id={`${view.id}:body`}>
      {objectRef ? <ViewBodyObjectContext objectRef={objectRef} /> : null}
      {children}
    </ViewIsland>
  );
};

/** The existing page island publishes metadata from its loaded object, never from a requested route. */
function ViewBodyObjectContext({ objectRef }: { objectRef: UiObjectRef }) {
  const view = useService(ViewService).view;
  const context = useService(WorkspaceViewContextService);
  const location = useLiveData(view.location$);
  const route = location.pathname + location.search + location.hash;
  const { kind, id, docId, attributes } = objectRef;
  const reference = useMemo(
    () => ({
      kind,
      id,
      ...(docId !== undefined ? { docId } : {}),
      ...(attributes !== undefined ? { attributes } : {}),
    }),
    [kind, id, docId, attributes]
  );
  useEffect(() => {
    const scope = { workspaceId: context.workspaceId, viewId: view.id };
    const ownerId = `${reference.kind}:${reference.id}`;
    return context.register(
      scope,
      ownerId,
      () => ({
        surfaceId: ownerId,
        route,
        title: view.title$.value,
        summary: '',
        refs: [reference],
        elements: [],
        interactions: [],
        actions: [],
      }),
      {
        kind: 'surface',
        isActive: () => {
          const current = view.location$.value;
          return current.pathname + current.search + current.hash === route;
        },
      }
    );
  }, [context, view, route, reference]);
  return null;
}

export const ViewBodyTarget = forwardRef(function ViewBodyTarget(
  {
    viewId,
    ...otherProps
  }: React.HTMLProps<HTMLDivElement> & { viewId: string },
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const region = useSemanticDomRegion('main-dom', viewId);
  useImperativeHandle(ref, () => region.root as HTMLDivElement, [region.root]);

  return (
    <>
      <ViewIslandTarget
        id={`${viewId}:body`}
        {...otherProps}
        ref={region.ref}
        {...region.attributes}
      />
      <WorkspaceNavigationPresence viewId={viewId} />
    </>
  );
});

export const ViewHeader = ({ children }: React.PropsWithChildren) => {
  const view = useService(ViewService).view;

  return <ViewIsland id={`${view.id}:header`}>{children}</ViewIsland>;
};

export const ViewHeaderTarget = forwardRef(function ViewHeaderTarget(
  {
    viewId,
    ...otherProps
  }: React.HTMLProps<HTMLDivElement> & { viewId: string },
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const region = useSemanticDomRegion('main-header', viewId);
  useImperativeHandle(ref, () => region.root as HTMLDivElement, [region.root]);
  return (
    <ViewIslandTarget
      id={`${viewId}:header`}
      {...otherProps}
      ref={region.ref}
      {...region.attributes}
    />
  );
});

/** Workspace-owned content is portalled into the actual active main view. */
export const ViewCopilotOverlay = ({ children }: React.PropsWithChildren) => {
  const view = useService(ViewService).view;
  return <ViewIsland id={`${view.id}:copilot`}>{children}</ViewIsland>;
};

/**
 * Copilot's own overlay spans the view's content, like the AI island's row below it. It is no
 * picking root: Copilot itself is never picked as context.
 */
export const ViewCopilotOverlayTarget = ({ view }: { view: View }) => {
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  useViewContentInline(root, view);
  return (
    <ViewIslandTarget
      id={`${view.id}:copilot`}
      ref={setRoot}
      data-workspace-view-id={view.id}
      style={{ position: 'absolute', top: 0, bottom: 0, pointerEvents: 'none' }}
    />
  );
};

export const ViewSidebarTab = ({
  children,
  tabId,
  icon,
  label,
  unmountOnInactive = true,
}: React.PropsWithChildren<{
  tabId: string;
  icon: React.ReactNode;
  label?: string;
  unmountOnInactive?: boolean;
}>) => {
  const view = useService(ViewService).view;
  const workbench = useService(WorkbenchService).workbench;
  const sidebarOpened = useLiveData(workbench.sidebarOpen$);
  const activeTab = useLiveData(view.activeSidebarTab$);

  const isActive = activeTab?.id === tabId && sidebarOpened;
  // A new icon element each render must not register the tab again.
  const iconRef = useRef(icon);
  useLayoutEffect(() => {
    iconRef.current = icon;
  });

  useEffect(() => {
    view.addSidebarTab(tabId, label, iconRef.current);
    return () => {
      view.removeSidebarTab(tabId);
    };
  }, [label, tabId, view]);

  return (
    <>
      <ViewIsland id={`${view.id}:sidebar:${tabId}:icon`}>{icon}</ViewIsland>
      <ViewIsland id={`${view.id}:sidebar:${tabId}:body`}>
        {unmountOnInactive && !isActive ? null : children}
      </ViewIsland>
    </>
  );
};

export const ViewSidebarTabIconTarget = forwardRef(
  function ViewSidebarTabIconTarget(
    {
      viewId,
      tabId,
      ...otherProps
    }: React.HTMLProps<HTMLDivElement> & { tabId: string; viewId: string },
    ref: Ref<HTMLDivElement>
  ) {
    return (
      <ViewIslandTarget
        ref={ref}
        id={`${viewId}:sidebar:${tabId}:icon`}
        {...otherProps}
      />
    );
  }
);

export const ViewSidebarTabBodyTarget = forwardRef(
  function ViewSidebarTabBodyTarget(
    {
      viewId,
      tabId,
      ...otherProps
    }: React.HTMLProps<HTMLDivElement> & {
      tabId: string;
      viewId: string;
    },
    ref: Ref<HTMLDivElement>
  ) {
    return (
      <ViewIslandTarget
        ref={ref}
        id={`${viewId}:sidebar:${tabId}:body`}
        {...otherProps}
      />
    );
  }
);
